import { useEffect, useState } from 'react';
import { Spin, Table, Tag, Space, Button, Alert } from 'antd';
import { useAuth } from '../../Contexts/AuthContext';
import { TextService } from '../../Services/TextService';
import LeftMenuCommunicate from './LeftCommunicate';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import { BatchJobService } from '../../Services/BatchJobService';
import { JobType } from '../../Types/JobType';
import { BatchStatus } from '../../Types/BatchStatus';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const AdminBatchJobHistory = () => {
    const { user } = useAuth();
    const [jobs, setJobs] = useState([] as any[]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    const GetText = TextService.GetText;

    useEffect(() => {
        loadJobs();
    }, []);

    const loadJobs = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const jobsData = await BatchJobService.GetHistory();
            setJobs(jobsData || []);
            setRetryCount(0); // Reset retry count on success
        } catch (error) {
            console.error('Error loading batch jobs:', error);
            const newRetryCount = retryCount + 1;
            setRetryCount(newRetryCount);
            setError(GetText("BatchJob_ErrorLoading"));
            setJobs([]);
        }
        setIsLoading(false);
    };

    const getJobTypeLabel = (jobType: number) => {
        switch (jobType) {
            case JobType.Unspecified:
                return GetText("BatchJob_TypeUnspecified");
            case JobType.SendEmail:
                return GetText("BatchJob_TypeEmail");
            case JobType.CreatePaymentLinks:
                return GetText("BatchJob_TypePaymentLinks");
            case JobType.SendSms:
                return GetText("BatchJob_TypeSms");
            default:
                return `Type ${jobType}`;
        }
    };

    const getStatusTag = (status: number) => {
        switch (status) {
            case BatchStatus.Pending:
                return <Tag color="blue">{GetText("BatchJob_StatusPending")}</Tag>;
            case BatchStatus.Processing:
                return <Tag color="orange">{GetText("BatchJob_StatusProcessing")}</Tag>;
            case BatchStatus.Completed:
                return <Tag color="green">{GetText("BatchJob_StatusCompleted")}</Tag>;
            case BatchStatus.Failed:
                return <Tag>{status}</Tag>;
        }
    };

    const columns = [
        {
            title: GetText("BatchJob_ColumnCreated"),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-',
            sorter: (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            defaultSortOrder: 'ascend' as const,
        },
        {
            title: GetText("BatchJob_ColumnType"),
            dataIndex: 'jobType',
            key: 'jobType',
            render: (jobType: number) => getJobTypeLabel(jobType),
        },
        {
            title: GetText("BatchJob_ColumnStatus"),
            dataIndex: 'status',
            key: 'status',
            render: (status: number) => getStatusTag(status),
        },
        {
            title: GetText("BatchJob_ColumnProgress"),
            key: 'progress',
            render: (record: any) => {
                if (record.totalItems > 0) {
                    const completed = record.completedItems || 0;
                    return `${completed}/${record.totalItems}`;
                }
                return '-';
            },
        },
        {
            title: GetText("BatchJob_ColumnFailures"),
            key: 'failures',
            render: (record: any) => {
                const failureCount = record.failureCount || 0;
                if (failureCount > 0) {
                    return <Tag color="red">{failureCount}</Tag>;
                }
                return <Tag color="green">0</Tag>;
            },
        },
        {
            title: GetText("BatchJob_ColumnCompleted"),
            dataIndex: 'completedAt',
            key: 'completedAt',
            render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: GetText("BatchJob_ColumnError"),
            dataIndex: 'errorMessage',
            key: 'errorMessage',
            render: (error: string) => error ? <Tag color="red">{error}</Tag> : '-',
        },
    ];

    return (
        <LayoutLeftMenu
            title={GetText("BatchJob_Title")}
            menu={<LeftMenuCommunicate selected={"batchJobHistory"} />}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>{GetText("BatchJob_Description")}</h2>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={loadJobs}
                        loading={isLoading}
                    >
                        {GetText("Common_Refresh")}
                    </Button>
                </div>

                {error && (
                    <Alert
                        message={GetText("Common_Error")}
                        description={retryCount > 1 ? `${error} (Attempt ${retryCount})` : error}
                        type="error"
                        showIcon
                        closable
                        onClose={() => setError(null)}
                    />
                )}

                <Table
                    dataSource={jobs}
                    columns={columns}
                    loading={isLoading}
                    rowKey="id"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `${GetText("Common_Total")} ${total} ${GetText("BatchJob_Jobs")}`,
                    }}
                />
            </Space>
        </LayoutLeftMenu>
    );
};

export default AdminBatchJobHistory;
